import { createContext, useContext, useState } from 'react';

export const LANGS = {
  en: { name: 'English',    cc: 'us', code: 'EN' },
  ko: { name: '한국어',     cc: 'kr', code: 'KO' },
  zh: { name: '中文',       cc: 'cn', code: 'ZH' },
  ja: { name: '日本語',     cc: 'jp', code: 'JA' },
  es: { name: 'Español',    cc: 'es', code: 'ES' },
  it: { name: 'Italiano',   cc: 'it', code: 'IT' },
  de: { name: 'Deutsch',    cc: 'de', code: 'DE' },
  nl: { name: 'Nederlands', cc: 'nl', code: 'NL' },
};

// browser language tag → our lang code
const BROWSER_LANG_MAP = {
  ko: 'ko', 'ko-kr': 'ko',
  zh: 'zh', 'zh-cn': 'zh', 'zh-tw': 'zh', 'zh-hk': 'zh', 'zh-sg': 'zh',
  ja: 'ja', 'ja-jp': 'ja',
  es: 'es', 'es-es': 'es', 'es-mx': 'es', 'es-ar': 'es', 'es-co': 'es',
              'es-cl': 'es', 'es-pe': 'es', 'es-419': 'es',
  it: 'it', 'it-it': 'it', 'it-ch': 'it',
  de: 'de', 'de-de': 'de', 'de-at': 'de', 'de-ch': 'de',
  nl: 'nl', 'nl-nl': 'nl', 'nl-be': 'nl',
};

function detectGeoLang() {
  const navLang = (navigator.language || navigator.languages?.[0] || 'en').toLowerCase();
  const primary = navLang.split('-')[0];
  return BROWSER_LANG_MAP[navLang] || BROWSER_LANG_MAP[primary] || 'en';
}

function getInitialState() {
  const geoLang = detectGeoLang();
  const saved = localStorage.getItem('xv_lang');
  if (saved && LANGS[saved]) {
    // User manually chose a language — honour it for UI
    return { lang: saved, geoLang };
  }
  // Korea exception: geo=ko → UI defaults to English, SEO stays Korean
  const lang = geoLang === 'ko' ? 'en' : geoLang;
  return { lang, geoLang };
}

const Ctx = createContext({ lang: 'en', geoLang: 'en', setLang: () => {} });

export function LanguageProvider({ children }) {
  const [state] = useState(getInitialState);
  const [lang, setLangState] = useState(state.lang);
  const geoLang = state.geoLang;

  const setLang = (l) => {
    if (LANGS[l]) {
      localStorage.setItem('xv_lang', l);
      setLangState(l);
    }
  };

  return <Ctx.Provider value={{ lang, geoLang, setLang }}>{children}</Ctx.Provider>;
}

export function useLanguage() {
  const { lang, geoLang, setLang } = useContext(Ctx);
  // Object form: t({ ko, en, zh, ja, es, it })
  // Legacy form:  t(ko, en)  →  non-ko langs fall back to en
  const t = (koOrObj, en) => {
    if (koOrObj && typeof koOrObj === 'object') {
      return koOrObj[lang] ?? koOrObj.en ?? koOrObj.ko ?? '';
    }
    if (lang === 'ko') return koOrObj;
    return en ?? koOrObj;
  };
  return { lang, geoLang, setLang, t };
}
